import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useFetch } from "../lib/api";
import { useShop } from "../lib/store";
import { chf } from "../lib/format";
import { Seo, Loading, Empty, Stars } from "../components/Common";
import { ProductCard } from "../components/ProductCard";

export const Wishlist = () => {
  const { t, lang } = useI18n();
  const { wishlist, addToCart } = useShop();
  const slugs = wishlist.map((w) => w.slug).join(",");
  const { data, loading } = useFetch(slugs ? `/products-by-slugs?slugs=${slugs}&lang=${lang}` : null);
  return (
    <div className="container-hw py-10" data-testid="wishlist-page">
      <Seo title={t("wishlist")} noindex />
      <h1 className="text-4xl sm:text-5xl mb-8">{t("wishlist")}</h1>
      {!slugs ? <Empty title={t("wishlist_empty")} action={<Link to="/products" className="btn-primary">{t("continue_shopping")}</Link>} /> : loading || !data ? <Loading /> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10">
          {data.map((p) => (
            <div key={p.id}><ProductCard p={p} />
              <button className="btn-outline w-full h-10 mt-3" disabled={!p.in_stock} onClick={() => { addToCart(p.default_sku, 1, p.slug); toast.success(t("added_cart")); }} data-testid={`wishlist-add-to-cart-${p.slug}`}>{t("add_to_cart")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ROWS = [["price", "price"], ["rating", "rating"], ["availability", "availability"], ["brand", "brand"], ["width", "width", "cm"], ["depth", "depth", "cm"], ["height", "height", "cm"], ["weight", "weight", "kg"],
  ["material", "material", "m"], ["frame_material", "frame_material", "m"], ["upholstery", "upholstery", "m"], ["finish", "finish", "fi"], ["drawers", "drawers"], ["seats", "seats"], ["door_type", "door_type", "d"],
  ["size", "sizes"], ["color", "colors"], ["assembly_required", "assembly_required"], ["max_load", "max_load", "kg"]];

export const Compare = () => {
  const { t, lang, val } = useI18n();
  const { compare, toggleCompare, setCompare } = useShop();
  const { data } = useFetch(compare.length ? `/products-by-slugs?slugs=${compare.join(",")}&lang=${lang}` : null);
  const cell = (p, [label, key, unit]) => {
    const a = p.attributes;
    if (key === "price") return <span className="font-semibold">{chf(p.price)}</span>;
    if (key === "rating") return <Stars value={p.rating_avg} count={p.review_count} />;
    if (key === "availability") return p.in_stock ? t("in_stock") : t("out_of_stock");
    if (key === "brand") return p.brand;
    if (key === "sizes") return p.sizes.map((s) => val("s", s)).join(", ") || t("not_applicable");
    if (key === "colors") return p.colors.map((c) => val("c", c)).join(", ");
    const v = a[key];
    if (v == null || v === "") return t("not_applicable");
    if (typeof v === "boolean") return v ? t("yes") : t("no");
    if (unit === "cm" || unit === "kg") return `${v} ${unit}`;
    return unit ? val(unit, v) : v;
  };
  const types = data ? [...new Set(data.map((p) => p.type))] : [];
  return (
    <div className="container-hw py-10" data-testid="compare-page">
      <Seo title={t("compare")} noindex />
      <div className="flex justify-between items-end mb-8"><h1 className="text-4xl sm:text-5xl">{t("compare")}</h1>{compare.length > 0 && <button className="text-sm link-u" onClick={() => setCompare([])} data-testid="compare-clear">{t("clear_all")}</button>}</div>
      {!compare.length ? <Empty title={t("compare_empty")} action={<Link to="/c/beds" className="btn-primary">{t("continue_shopping")}</Link>} /> : !data ? <Loading /> : (
        <div className="overflow-x-auto">
          {types.length > 1 && <p className="text-xs text-[color:var(--hw-muted)] mb-4">{t("overall_dims_note")}</p>}
          <table className="w-full text-sm min-w-[640px]" data-testid="compare-table">
            <thead><tr><th className="w-40" />{data.map((p) => (
              <th key={p.slug} className="text-left align-top p-3 font-normal">
                <div className="relative"><button aria-label={t("remove")} onClick={() => toggleCompare(p.slug)} className="absolute top-1 right-1 bg-white rounded-full w-7 h-7 grid place-items-center" data-testid={`compare-remove-${p.slug}`}><X size={14} /></button>
                  <Link to={`/p/${p.slug}`}><img src={p.image} alt={p.name} className="w-full aspect-[4/5] object-cover mb-2" /><span className="font-medium hover:underline">{p.name}</span></Link></div>
              </th>))}</tr></thead>
            <tbody>{ROWS.filter((r) => data.some((p) => { const c = cell(p, r); return c !== t("not_applicable"); })).map((r) => (
              <tr key={r[0]} className="border-t border-[color:var(--hw-line)]"><th scope="row" className="text-left py-3 pr-3 font-medium text-[color:var(--hw-muted)]">{t(r[0])}</th>{data.map((p) => <td key={p.slug} className="p-3">{cell(p, r)}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
